import { parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Best-effort normalization of a phone string to E.164 (e.g. "+14155551234").
 *
 * The "Phone Number (new)" widget (react-phone-number-input) is a controlled
 * component whose value must be E.164. Prefilled values (URL params, defaults)
 * may arrive in any format, so we normalize them once on mount. Returns '' when
 * the input can't be parsed into a phone number.
 *
 * @param {string} value  Raw phone string.
 * @param {string} [iso2] ISO 3166-1 alpha-2 region used when `value` isn't E.164.
 * @returns {string} E.164 number, or '' if unparseable.
 */
export function normalizeToE164(value, iso2) {
  if (!value || typeof value !== 'string') return '';
  if (value[0] === '+') return value;
  const parsed = parsePhoneNumberFromString(value, iso2 ? iso2.toUpperCase() : undefined);
  return parsed ? parsed.number : '';
}

/**
 * Format an E.164 number for storage/results as "+<callingCode> <nationalNumber>"
 * (e.g. "+506 123213123123") so the country calling code is readable at a glance.
 * Falls back to the input unchanged when it can't be parsed.
 */
export function formatPhoneForStorage(value) {
  if (!value || typeof value !== 'string') return value || '';
  const parsed = parsePhoneNumberFromString(value);
  return parsed ? `+${parsed.countryCallingCode} ${parsed.nationalNumber}` : value;
}

/**
 * Strip formatting (spaces) back to E.164 — react-phone-number-input's `value`
 * prop requires strict E.164.
 */
export function toE164(value) {
  return typeof value === 'string' ? value.replace(/\s+/g, '') : value;
}

/**
 * Structure-based phone validation.
 *
 * The old phone field validated against react-intl-tel-input's bundled
 * libphonenumber metadata (frozen at April 2019), which rejected any area code
 * assigned after that date and blocked form submission. Strict validation
 * enumerates *assigned* number ranges, so it structurally lags real-world
 * numbering-plan changes no matter how fresh the metadata is.
 *
 * Instead we validate by structure: the number must be the correct length for
 * its country and, for NANP (+1) numbers, have a well-formed area code/exchange
 * (both starting 2-9). This accepts every real number — including newly assigned
 * area codes — while still rejecting clearly-impossible input (e.g. a 000 area
 * code, wrong length). It never needs a metadata refresh.
 *
 * @param {string} fullNumber  Number to check (E.164 preferred; a national number
 *                             works when `iso2` is supplied).
 * @param {string} [iso2]      ISO 3166-1 alpha-2 default region.
 * @returns {boolean}
 */
export default function isValidPhone(fullNumber, iso2) {
  if (!fullNumber || typeof fullNumber !== 'string') return false;

  const parsed = parsePhoneNumberFromString(fullNumber, iso2 ? iso2.toUpperCase() : undefined);
  if (!parsed) return false;

  // Correct length for the detected country.
  if (!parsed.isPossible()) return false;

  // NANP: area code and exchange must start with 2-9 (rejects 000/111-style input).
  if (parsed.countryCallingCode === '1') {
    return /^[2-9]\d{2}[2-9]\d{6}$/.test(parsed.nationalNumber);
  }

  return true;
}
